/* eslint-disable react/prop-types */
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import Visibility from '../../components/Visibility';
import { Description, DivRelative, SubTitle } from '../../components/layouts';
import { buildImageURL, getTrack, pureTrans } from '../../helpers';
import LazyLoad from 'react-lazyload';

import './style.scss';

const images = [buildImageURL('/zoom-1.jpg'), buildImageURL('/ruler.svg')];
const IMAGE = buildImageURL('/zoom-img.jpg');

const wrapperVariants = {
  step1: {
    scale: 1,
    filter: 'brightness(1)'
  },
  step2: {
    scale: 1.5,
    filter: 'brightness(1)'
  },
  step3: {
    scale: 2,
    filter: 'brightness(1)'
  },
  step4: {
    scale: 4,
    // filter: 'brightness(0.3)'
    filter: 'brightness(1)'
  }
};

const RULER_VARIANTS = {
  D: {
    step1: {
      x: '-8.6%'
    },
    step2: {
      x: '-14.5%'
    },
    step3: {
      x: '-43.5%'
    },
    step4: {
      x: '-87%'
    }
  },
  T: {
    step1: {
      x: '-8.5%'
    },
    step2: {
      x: '-14.5%'
    },
    step3: {
      x: '-43.5%'
    },
    step4: {
      x: '-87%'
    }
  },
  M: {
    step1: {
      x: '-8.5%'
    },
    step2: {
      x: '-14.5%'
    },
    step3: {
      x: '-43.5%'
    },
    step4: {
      x: '-87%'
    }
  }
};

const textVariants = {
  visible: {
    scale: 1,
    opacity: 1,
    x: '-50%',
    y: 0
  },
  hidden: {
    scale: 0.98,
    opacity: 0,
    x: '-50%',
    y: 100
  }
};

const textVariantsMobile = {
  visible: {
    scale: 1,
    opacity: 1
  },
  hidden: {
    scale: 0.98,
    opacity: 0.5
  }
};

const STEPS = ['step1', 'step2', 'step3', 'step4'];

const track = getTrack({ block: 13 });

export function HighZoom() {
  const [mobile, setMobile] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const [progress, setProgress] = useState(-1);
  const [rulerKey, setRulerKey] = useState('D');
  const [imgIndex, setImgIndex] = useState(0);
  const [lastStep, setLastStep] = useState(0);



  const handleUpdate = useCallback(
    (e, { calculations }) => {
      const { width, onScreen, percentagePassed, direction } = calculations;
      setMobile(width <= 750);
      setOnScreen(onScreen);
      setProgress(percentagePassed);

      let rulerKey;
      if (width <= 750) {
        rulerKey = 'M';
      } else if (width <= 1600) {
        rulerKey = 'T';
      } else {
        rulerKey = 'D';
      }
      setRulerKey(rulerKey);
    },
    [setOnScreen, setProgress, setRulerKey]
  );

  const step = React.useMemo(() => {
    return pureTrans(progress, [0, 0.2, 0.4, 0.6, 0.8], [0, 1, 2, 3, 4], 0);
  }, [progress]);

  const rulerVariants = useMemo(() => {
    return RULER_VARIANTS[rulerKey];
  }, [rulerKey]);

  useMemo(() => {
    if (step >= 4) {
      return;
    }
    setLastStep(step);

    let from = lastStep * 5;
    let to = step * 5;
    let total = to - from;
    let d = total / Math.abs(total);
    let timeout = Math.abs(500 / total);
    setImgIndex(from);
    let timer = setInterval(() => {
      if (from === to) {
        timer && clearInterval(timer);
        return;
      }
      from = from + d;
      setImgIndex(from);
    }, timeout);
  }, [step])

  return (
    <Visibility offset={[-1000, 1000]} fireOnMount onUpdate={handleUpdate} className="page-section section-high-zoom inverted" data-track={track}>
      <div className="sticky-content">
        <DivRelative>
          <div className="picture-wrapper">
            <LazyLoad>
              <motion.div
                initial={!onScreen}
                className="zoom-i-wrapper"
                variants={wrapperVariants}
                // animate={STEPS[step]}
                transition={{
                  scale: { duration: 0.5 },
                  filter: { duration: 0.5 }
                }}
              >
                <motion.div
                  className="zoom-i"
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{
                    // backgroundImage: `url(${images[0]})`
                    backgroundImage: `url(${IMAGE})`,
                    backgroundPosition: `center calc(0px - ${imgIndex} * var(--h))`
                  }}
                />
              </motion.div>
            </LazyLoad>            

            <AnimatePresence initial={!onScreen}>
              <div className="ruler-wrapper">
                <div className="ruler">
                  {mobile && (
                    <motion.div
                      initial="step1"
                      animate={STEPS[step]}
                      variants={RULER_VARIANTS.M}
                      transition={{ x: { duration: 0.5 } }}
                      style={{ backgroundImage: `url(${images[1]})` }}
                    />
                  )}
                  {!mobile && (
                    <motion.div
                      initial="step1"
                      animate={STEPS[step]}
                      variants={rulerVariants}
                      transition={{ x: { duration: 0.5 } }}
                      style={{ backgroundImage: `url(${images[1]})` }}
                    />
                  )}
                </div>
              </div>
            </AnimatePresence>
          </div>
          {!mobile && (
            <motion.div
              className="section-content center"
              animate={'visible'}
              variants={textVariants}
              transition={{
                scale: { delay: 1, duration: 0.5 },
                y: { delay: 1, duration: 0.5 },
                opacity: { delay: 1, duration: 0.5 }
              }}
            >
              <SubTitle>
                留下，远处神秘的身影
              </SubTitle>
              <Description>实现5倍光学变焦、60倍超级变焦，你想看到远方的人、景色，都能轻松拍出。</Description>
            </motion.div>
          )}
          {mobile && (
            <motion.div
              className="section-content center"
              animate={step === 3 ? 'visible' : 'hidden'}
              variants={textVariantsMobile}
              transition={{
                scale: { delay: 1, duration: 0.5 },
                opacity: { delay: 1, duration: 0.5 }
              }}
            >
              <SubTitle>
                留下
                <br />
                远处神秘的身影
              </SubTitle>
              <Description>实现5倍光学变焦、60倍超级变焦，你想看到远方的人、景色，都能轻松拍出。</Description>
            </motion.div>
          )}
        </DivRelative>
      </div>
    </Visibility>
  );
}
